import React, { useState, useEffect, useMemo } from 'react';
import { useBranch } from '../../../layouts/BranchContext';
import {
  styled,
  Button,
  TextField,
  Tooltip,
  Container,
  Typography,
  InputAdornment,
  Box,
  Table,
  Modal,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
} from '@mui/material';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Close as CloseIcon,
  KeyboardDoubleArrowLeft as ChevronLeft,
  KeyboardDoubleArrowRight as ChevronRight,
} from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const CustomTableScrollbar = styled('div')(({ theme }) => ({
  '&::-webkit-scrollbar': {
    height: '6px',
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'} transparent`,
}));

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const Module = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const { selectedBranchId, branchList, isMainBranchAdmin } = useBranch();
const canSwitchBranch = isMainBranchAdmin;
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const rowsPerPageOptions = [5, 10, 25, 50];
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [showUserGuideModal, setShowUserGuideModal] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddModuleModal, setShowAddModuleModal] = useState(false);
  const [showEditModuleModal, setShowEditModuleModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalType, setSuccessModalType] = useState(''); // 'add' or 'edit'
  const [selectedModule, setSelectedModule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [courseMap, setCourseMap] = useState({}); // id → name
  const [formData, setFormData] = useState({
  courseId: '',
  moduleName: '',
  description: '',   // ← new field
  });
    const [modules, setModules] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  // ===== End Branch Dropdown state =====

  const getOrganizationId = () => {
    try {
      const raw = sessionStorage.getItem('user') || localStorage.getItem('user') || '{}';
      const user = JSON.parse(raw) || {};
      return user.organization_id ?? null;
    } catch {
      return null;
    }
  };

    const buildOrgBranchQuery = (organizationId, branchId) => {
    const params = new URLSearchParams();
    if (organizationId !== null && organizationId !== undefined && organizationId !== '') {
      params.append('organization', organizationId);
    }
    if (branchId !== null && branchId !== undefined && branchId !== '') {
      params.append('branch', branchId);
    }
    const query = params.toString();
    return query ? `?${query}` : '';
  };

  // ===== End Branch Dropdown state =====

useEffect(() => {
  const fetchData = async () => {
    setLoading(true);
    const token = localStorage.getItem('accessToken');

    try {
      // Fetch COURSES with branch filter
      const branchParam = selectedBranchId ? `?branch=${selectedBranchId}` : '';
      const coursesRes = await axios.get(`${API_BASE_URL}/api/course/courses/${branchParam}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      const loadedCourses = Array.isArray(coursesRes.data)
        ? coursesRes.data
        : (coursesRes.data?.results || []);
      setCourses(loadedCourses);

      // Build courseMap for quick name lookup in table
      const map = {};
      loadedCourses.forEach(c => {
        map[c.id] = c.course_name || c.name || c.title || 'Unnamed Course';
      });
      setCourseMap(map);

           
      const organizationId = getOrganizationId();
      const moduleQuery = buildOrgBranchQuery(organizationId, selectedBranchId);

      const modulesRes = await axios.get(`${API_BASE_URL}/api/course/modules/${moduleQuery}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      const loadedModules = Array.isArray(modulesRes.data) ? modulesRes.data : [];
      setModules(loadedModules);
    } catch (err) {
      console.error("Failed to load data:", err);
      setSnackbarMessage("Failed to load courses or modules");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  fetchData();
}, [selectedBranchId]); // Re-fetch when branch changes

const handleAddModule = () => {
  setFormData({
    courseId: '',
    moduleName: '',
    description: '',   // ← reset
  });
  setShowAddModuleModal(true);
};

const handleEdit = (module) => {
  setSelectedModule(module);
  setFormData({
    courseId: (module.course || module.course_name || module.CourseId || '').toString(),
    moduleName: module.name || module.module_name || module.ModuleName || '',
    description: module.description || module.module_description || module.desc || '',  // ← add this
  });
  setShowEditModuleModal(true);
};

  const handleShowDeleteModal = (module) => {
    setSelectedModule(module);
    setShowDeleteModal(true);
  };

const handleDelete = async () => {
  if (!selectedModule?.id) return;

  try {
    const token = localStorage.getItem('accessToken');
    await axios.delete(`${API_BASE_URL}/api/course/modules/${selectedModule.id}/`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

       // Refresh list, scoped to current organization + branch
    const moduleQuery = buildOrgBranchQuery(getOrganizationId(), selectedBranchId);
    const res = await axios.get(`${API_BASE_URL}/api/course/modules/${moduleQuery}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const remainingModules = Array.isArray(res.data) ? res.data : [];
    setModules(remainingModules);

    setShowDeleteModal(false);
    setSelectedModule(null);
    setSnackbarMessage("Module deleted successfully");
    setSnackbarSeverity("success");
    setOpenSnackbar(true);
  } catch (err) {
    setSnackbarMessage("Failed to delete module");
    setSnackbarSeverity("error");
    setOpenSnackbar(true);
  }
};

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedModule(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

const handleSave = async (e) => {
  e.preventDefault();
  if (!formData.courseId || !formData.moduleName.trim()) {
    setSnackbarMessage("Please select course and enter module name");
    setSnackbarSeverity("error");
    setOpenSnackbar(true);
    return;
  }

  try {
    const token = localStorage.getItem('accessToken');
    const organizationId = getOrganizationId();

    await axios.post(
      `${API_BASE_URL}/api/course/modules/`,
      {
        course: parseInt(formData.courseId),
        module_name: formData.moduleName.trim(),
        module_description: formData.description.trim(),
        organization: organizationId,
        branch: selectedBranchId ?? null,
      },
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );

        // Refresh modules list, scoped to current organization + branch
    const moduleQuery = buildOrgBranchQuery(organizationId, selectedBranchId);
    const res = await axios.get(`${API_BASE_URL}/api/course/modules/${moduleQuery}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const newModules = Array.isArray(res.data) ? res.data : [];
    setModules(newModules);

    setShowAddModuleModal(false);
    setFormData({ courseId: '', moduleName: '', description: '' });
    setSnackbarMessage("Module added successfully");
    setSnackbarSeverity("success");
    setOpenSnackbar(true);
  } catch (err) {
    setSnackbarMessage("Failed to add module");
    setSnackbarSeverity("error");
    setOpenSnackbar(true);
  }
};

const handleUpdate = async (e) => {
  e.preventDefault();
  if (!formData.courseId || !formData.moduleName.trim() || !selectedModule?.id) {
    setSnackbarMessage("Please fill all required fields");
    setSnackbarSeverity("error");
    setOpenSnackbar(true);
    return;
  }

  try {
    const token = localStorage.getItem('accessToken');
    const organizationId = getOrganizationId();

    await axios.put(
      `${API_BASE_URL}/api/course/modules/${selectedModule.id}/`,
      {
        course: parseInt(formData.courseId),
        module_name: formData.moduleName.trim(),
        module_description: formData.description.trim(),
        organization: selectedModule?.organization ?? organizationId,
        branch: selectedModule?.branch ?? selectedBranchId ?? null,
      },
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );

        // Refresh list, scoped to current organization + branch
    const moduleQuery = buildOrgBranchQuery(organizationId, selectedBranchId);
    const res = await axios.get(`${API_BASE_URL}/api/course/modules/${moduleQuery}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const updatedModules = Array.isArray(res.data) ? res.data : [];
    setModules(updatedModules);

    setShowEditModuleModal(false);
    setFormData({ courseId: '', moduleName: '', description: '' });
    setSelectedModule(null);
    setSnackbarMessage("Module updated successfully");
    setSnackbarSeverity("success");
    setOpenSnackbar(true);
  } catch (err) {
    setSnackbarMessage("Failed to update module");
    setSnackbarSeverity("error");
    setOpenSnackbar(true);
  }
}; 

  const handleReset = () => {
    setFormData({
      courseId: '',
      moduleName: '',
    });
  };

  const handleCancel = () => {
    setShowEditModuleModal(false);
    setFormData({
      courseId: '',
      moduleName: '',
    });
    setSelectedModule(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

useEffect(() => {
  const term = searchTerm.toLowerCase().trim();

  const filtered = modules.filter((module) => {
    // Get course ID safely
    const courseId = module.course || module.course_id || module.CourseId || null;

    // Get course name from map (fallback to empty string)
    const courseName = (courseMap[courseId] || '').toLowerCase();

    // Get module name safely (use whatever your API actually returns)
    const moduleName = (
      module.name ||
      module.module_name ||
      module.ModuleName ||
      module.title ||
      ''
    ).toLowerCase();

    return courseName.includes(term) || moduleName.includes(term);
  });

  setFilteredData(filtered);
  setPage(1);
}, [searchTerm, modules, courseMap]);

  const paginatedData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <Container>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 1,
          p: 2,
          boxShadow: 'none',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center',gap:2}}>
       <TextField
  variant="outlined"
  placeholder="Search..."
  value={searchTerm}
  onChange={(e) => setSearchTerm(e.target.value)}
  InputProps={{
    startAdornment: (
      <InputAdornment position="start">
        <SearchIcon
          sx={{
            color: theme.palette.primary.main,
            marginLeft: 1.5,
            fontSize: 24,
          }}
        />
      </InputAdornment>
    ),
    endAdornment: searchTerm && (
      <InputAdornment position="end">
        <IconButton
          size="small"
          onClick={() => setSearchTerm('')}
          sx={{
            color: "grey",
            marginRight: 1,
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </InputAdornment>
    ),
    sx: {
      borderRadius: '30px',
      backgroundColor: theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.15)'
        : 'rgba(255, 255, 255, 1)',
      boxShadow: theme.palette.mode === 'dark'
        ? '0 2px 8px rgba(0, 0, 0, 0.3)'
        : '0 2px 8px rgba(0, 0, 0, 0.1)',
      '& .MuiOutlinedInput-notchedOutline': {
        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
      },
      '&:hover': {
        backgroundColor: theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.2)'
          : 'rgba(240, 240, 240, 1)',
        boxShadow: theme.palette.mode === 'dark'
          ? '0 2px 12px rgba(0, 0, 0, 0.4)'
          : '0 2px 12px rgba(0, 0, 0, 0.15)',
      },
      '&.Mui-focused': {
        backgroundColor: theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.25)'
          : 'rgba(255, 255, 255, 1)',
        boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`,
        '& .MuiOutlinedInput-notchedOutline': {
          border: `2px solid ${theme.palette.primary.main}`,
        },
      },
      paddingLeft: 1.5,
      height: '50px',
      transition: 'all 0.3s ease',
    },
  }}
  sx={{
    width: 350,
    maxWidth: '100%',
    '& .MuiInputBase-input': {
      padding: '10px 14px',
      color: theme.palette.text.primary,
      fontWeight: 500,
    },
  }}
/>

        <Button
          variant="contained"
          onClick={handleAddModule}
          sx={{
            ml: 2,
            width: '160px',
            borderRadius:'10px',
            height: '50px',
            padding: '10px 0',
            fontWeight: 'bold',
            backgroundColor: theme.palette.primary.main,
            '&:hover': { backgroundColor: theme.palette.primary.dark },
          }}
          startIcon={<AddIcon />}
        >
          ADD MODULE
        </Button>
        </Box>
        <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <IconButton
                    onClick={() => setShowUserGuideModal(true)}
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      minWidth: 0, // Remove MUI's default padding
                    }}
                  >
                    <InfoOutlineIcon sx={{ fontSize: 30 }} />
                  </IconButton>
                </Box>
      </Box>
<Paper
        elevation={3}
        sx={{
          paddingBottom: 0,
          maxWidth: '100%',
          boxSizing: 'border-box',
          mx: 'auto',
          backgroundColor: theme.palette.background.paper,
          borderRadius: '16px',
        }}
      >
        <CustomTableScrollbar sx={{ width: '100%', overflowX: 'auto' }}>
          <TableContainer sx={{ width: '100%', borderRadius: '16px' }}>
            <Table
              sx={{
                minWidth: 650,
                '& .MuiTableHead-root .MuiTableCell-root': {
                  fontWeight: 'bold',
                  backgroundColor: theme.palette.primary.main,
                  color: 'white',
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', minWidth: 70, whiteSpace: 'nowrap' }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', minWidth: 200, whiteSpace: 'nowrap' }}>Course</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', minWidth: 200, whiteSpace: 'nowrap' }}>Module</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', minWidth: 300, whiteSpace: 'normal' }}>
                  Description
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: theme.palette.primary.dark, fontSize: '1.1rem', minWidth: 120, whiteSpace: 'nowrap' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.length > 0 ? (
                  paginatedData.map((module, index) => (
                   <TableRow
  key={module.id || module.ModuleId || Math.random()} // fallback if no id
  sx={{
    backgroundColor: index % 2 === 0 ? theme.palette.background.paper : theme.palette.background.paper,
    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
  }}
>
  <TableCell sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>{(page - 1) * rowsPerPage + index + 1}</TableCell>
  <TableCell sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>
    {courseMap[module.course || module.course_id || module.CourseId] || '—'}
  </TableCell>

  <TableCell sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>
    {module.name || module.module_name || module.ModuleName || module.title || '—'}
  </TableCell>
<TableCell sx={{ fontSize: '1rem', whiteSpace: 'normal', wordBreak: 'break-word' }}>
  {module.module_description || module.description || '—'}
</TableCell>
  <TableCell align="center">
    <IconButton
      onClick={() => handleEdit(module)}
      sx={{
        color: theme.palette.primary.light,
        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
      }}
    >
      <EditIcon />
    </IconButton>
    <IconButton
      color="error"
      onClick={() => handleShowDeleteModal(module)}
      sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
    >
      <DeleteIcon />
    </IconButton>
  </TableCell>
</TableRow>
                  ))
                ) : (
                  <TableRow sx={{
                backgroundColor: theme.palette.background.paper,
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}>
                    
<TableCell colSpan={5} align="center" sx={{ fontSize: '1rem', padding: '20px', wordSpacing: 7 }}>
  No Data Found
</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CustomTableScrollbar>
      </Paper>

      {filteredData.length > 0 && (
        <Box
          sx={{
            maxWidth: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 1,
            p: 2,
            mx: 'auto',
            pt: 2,
          }}
        >
          <Typography variant="body2" color={theme.palette.primary.main}>
            Page <strong>{page}</strong> of <strong>{Math.ceil(filteredData.length / rowsPerPage)}</strong>
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 1}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <ChevronLeft />
            </IconButton>
            {[...Array(Math.ceil(filteredData.length / rowsPerPage))].map((_, index) => (
              <IconButton
                key={index + 1}
                onClick={() => handleChangePage(null, index + 1)}
                sx={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  backgroundColor: page === index + 1 ? theme.palette.primary.main : 'transparent',
                  color: page === index + 1 ? theme.palette.primary.contrastText : theme.palette.text.primary,
                  '&:hover': {
                    backgroundColor: page === index + 1 ? theme.palette.primary.dark : theme.palette.action.hover,
                  },
                }}
              >
                {index + 1}
              </IconButton>
            ))}
            <IconButton
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page >= Math.ceil(filteredData.length / rowsPerPage)}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <ChevronRight />
            </IconButton>
            <FormControl size="small" sx={{ minWidth: 100, ml: 1 }}>
              <InputLabel id="rows-per-page-label" sx={{ color: theme.palette.text.primary }}>Rows</InputLabel>
              <Select
                labelId="rows-per-page-label"
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
                label="Rows"
                sx={{
                  height: '36px',
                  color: theme.palette.text.primary,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.text.primary,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                {rowsPerPageOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Box>
      )}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Delete Modal */}
      <Modal
        open={showDeleteModal}
        onClose={handleCloseDeleteModal}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px' }}>
            <Typography id="delete-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Delete Confirmation ⚠️
            </Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography id="delete-modal-description" sx={{ mb: 2 }}>
              Are you sure you want to delete <strong>{selectedModule?.ModuleName}</strong>?
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button onClick={handleCloseDeleteModal} variant="outlined" sx={{ mr: 1 }} data-app-cancel="true">
                Cancel
              </Button>
              <Button variant="contained" color="error" onClick={handleDelete}>
                Yes, Delete
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Add Module Modal */}
      <Modal
        open={showAddModuleModal}
        onClose={() => setShowAddModuleModal(false)}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <CustomTableScrollbar
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              padding: '12px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
          >
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
              Add Module
            </Typography>
            <IconButton onClick={() => setShowAddModuleModal(false)} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ padding: '20px', overflowY: 'auto', flexGrow: 1 }}>
            <form onSubmit={handleSave}>
            <FormControl fullWidth sx={{ mb: 3 }}>
  <InputLabel id="course-label"
  sx={{ color: theme.palette.text.primary }}
  >Course *</InputLabel>
  <Select
    labelId="course-label"
    label="Course *"
    name="courseId"
    value={formData.courseId}
    onChange={handleChange}
    disabled={courses.length === 0}
  >
    {courses.length === 0 ? (
      <MenuItem disabled value="">
        {loading ? "Loading courses..." : "No courses available"}
      </MenuItem>
    ) : (
      courses.map((course) => (
        <MenuItem key={course.id} value={course.id}>
          {course.course_name || course.name || course.title || "Unnamed Course"}
        </MenuItem>
      ))
    )}
  </Select>
</FormControl>
              <TextField
                label="Module Name"
                variant="outlined"
                fullWidth
                name="moduleName"
                value={formData.moduleName}
                onChange={handleChange}
                sx={{
                  mb: 3,
                  '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                  '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                }}
              />
              <TextField
  label="Module Description"
  variant="outlined"
  fullWidth
  multiline
  rows={3}
  name="description"
  value={formData.description}
  onChange={handleChange}
  sx={{
    mb: 3,
    '& .MuiInputLabel-root': { color: theme.palette.text.primary },
    '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
  }}
/>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
               
                <Button variant="contained" sx={{mr: 2,}} color="error" onClick={handleReset}>
                  RESET
                </Button>
                 <Tooltip
                  title={!formData.courseId ? 'Select Course' : !formData.moduleName ? 'Enter Module Name' : ''}
                  arrow
                >
                  <span>
                    <Button
                      variant="contained"
                      onClick={handleSave}
                      sx={{
                        
                        backgroundColor: theme.palette.primary.main,
                        '&:hover': { backgroundColor: theme.palette.primary.dark },
                      }}
                      disabled={!formData.courseId || !formData.moduleName}
                    >
                      SAVE
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </form>
          </Box>
        </CustomTableScrollbar>
      </Modal>

      {/* Edit Module Modal */}
      <Modal
        open={showEditModuleModal}
        onClose={handleCancel}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <CustomTableScrollbar
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              padding: '12px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
          >
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
              Edit Module
            </Typography>
            <IconButton onClick={handleCancel} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ padding: '20px', overflowY: 'auto', flexGrow: 1 }}>
            <form onSubmit={handleUpdate}>
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel id="course-label" sx={{ color: theme.palette.text.primary }}>
                  Course
                </InputLabel>
              <Select
  value={formData.courseId}
  label="Course"
  name="courseId"
  onChange={handleChange}
>
  {courses.map((course) => (
    <MenuItem key={course.id} value={course.id}>
      {course.course_name || course.courseName|| 'Unnamed Course'}
    </MenuItem>
  ))}
</Select>
              </FormControl>
              <TextField
                label="Module Name"
                variant="outlined"
                fullWidth
                name="moduleName"
                value={formData.moduleName}
                onChange={handleChange}
                sx={{
                  mb: 3,
                  '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                  '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                }}
              />
              <TextField
  label="Module Description"
  variant="outlined"
  fullWidth
  multiline
  rows={3}
  name="description"
  value={formData.description}
  onChange={handleChange}
  sx={{
    mb: 3,
    '& .MuiInputLabel-root': { color: theme.palette.text.primary },
    '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
  }}
/>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
               
                <Button variant="contained" color="error" sx={{mr: 2,}} onClick={handleCancel}>
                  CANCEL
                </Button>
                 <Tooltip
                  title={!formData.courseId ? 'Select Course' : !formData.moduleName ? 'Enter Module Name' : ''}
                  arrow
                >
                  <span>
                    <Button
                      variant="contained"
                      onClick={handleUpdate}
                      sx={{
                        
                        backgroundColor: theme.palette.primary.main,
                        '&:hover': { backgroundColor: theme.palette.primary.dark },
                      }}
                      disabled={!formData.courseId || !formData.moduleName}
                    >
                      UPDATE
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </form>
          </Box>
        </CustomTableScrollbar>
      </Modal>

      {/* Success Modal */}
      <Modal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px' }}>
            <Typography id="success-modal-title" variant="h6" sx={{ color: '#fff' }}>
              {successModalType === 'add' ? 'Module Added ✅' : 'Module Updated ✅'}
            </Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography id="success-modal-description" sx={{ mb: 2 }}>
              {successModalType === 'add' ? 'Module Added Successfully' : 'Module Updated Successfully'}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                onClick={() => setShowSuccessModal(false)}
                variant="contained"
                color="primary"
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
      <Modal
        open={showUserGuideModal}
        onClose={() => setShowUserGuideModal(false)}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
  <Box
    sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 800,
      bgcolor: 'background.paper',
      borderRadius: 2,
      boxShadow: 24,
      overflow: 'hidden',
    }}
  >
    {/* Header */}
    <Box
      sx={{
        backgroundColor: theme.palette.primary.main,
        padding: '12px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'relative',
      }}
    >
      <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
        Module Management - User Guide
      </Typography>
      <IconButton
        onClick={() => setShowUserGuideModal(false)}
        sx={{ color: '#fff', position: 'absolute', right: 14 }}
      >
        <CloseIcon />
      </IconButton>
    </Box>

    {/* Scrollable Content */}
   <CustomTableScrollbar sx={{ maxHeight: '70vh', overflowY: 'auto', padding: '20px' }}>
  {/* Section 1: Add Module */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1 }}
  >
    ➤ Follow these steps to add a new module:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
    <li>Click the <strong>ADD MODULE</strong> button at the top right to open the add module modal.</li>
    <li>Select a Course from the dropdown menu.</li>
    <li>Enter the Module Name in the provided text field.</li>
    <li>Review the entered information for accuracy.</li>
    <li>Click the <strong>SAVE</strong> button to add the module. A success message will confirm the addition.</li>
    <li>To clear the form, click the <strong>RESET</strong> button.</li>
    <li>The new module will appear in the Module List table.</li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: Both Course and Module Name are required fields.
  </Typography>

  {/* Section 2: Edit Module */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
  >
    ➤ Follow these steps to edit an existing module:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
    <li>Select the module you want to edit from the Module List table.</li>
    <li>Click the <strong>Edit</strong> (pencil icon) in the Action column to open the edit module modal.</li>
    <li>Update the Course or Module Name as needed.</li>
    <li>Ensure both fields are filled.</li>
    <li>Click the <strong>UPDATE</strong> button to save changes. A success message will confirm the update.</li>
    <li>To discard changes, click the <strong>CANCEL</strong> button.</li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: Both Course and Module Name are required fields.
  </Typography>

  {/* Section 3: Delete Module */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
  >
    ➤ Follow these steps to delete a module:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
    <li>Select the module you want to delete from the Module List table.</li>
    <li>Click the <strong>Delete</strong> (trash icon) in the Action column.</li>
    <li>Confirm the deletion in the modal by clicking <strong>Yes, Delete</strong>.</li>
    <li>The module will be removed, and a success message will appear.</li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: Deleted modules cannot be recovered. Proceed with caution.
  </Typography>

  {/* Section 4: Search Modules */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
  >
    ➤ Follow these steps to search for a module:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
    <li>Use the search bar at the top left of the Module List.</li>
    <li>Enter the course name or module name to filter the table.</li>
    <li>The table will update automatically to show matching modules.</li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: The search is case-insensitive and filters by Course Name or Module Name.
  </Typography>

  {/* Section 5: Branch Dropdown */}
 <Typography
  variant="body1"
  sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
>
  ➤ Using the Branch selector:
</Typography>
<ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
  <li>The branch is now selected globally from the navbar and applies to all pages.</li>
  <li>The Course dropdown in Add/Edit Module will automatically show only courses belonging to the selected branch.</li>
  <li>Switch branches using the global branch selector in the navbar.</li>
</ul>

  {/* Section 6: Pagination */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
  >
    ➤ Follow these steps to navigate the module list:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
    <li>Use the pagination controls at the bottom to browse pages.</li>
    <li>Click the left (<strong>&lt;</strong>) or right (<strong>&gt;</strong>) arrows to switch pages.</li>
    <li>Select a specific page number by clicking on it.</li>
    <li>Adjust the number of rows per page using the dropdown menu (e.g., 5, 10, 25, 50 rows).</li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: The table displays only the selected number of rows per page.
  </Typography>

  {/* Bottom Close Button */}
  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
    {/* Close button can be added here if needed */}
  </Box>
</CustomTableScrollbar>

  </Box>
</Modal>
<Snackbar
  open={openSnackbar}
  autoHideDuration={4000}
  onClose={() => setOpenSnackbar(false)}
  anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
>
  <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
    {snackbarMessage}
  </Alert>
</Snackbar>
    </Container>
  );
};

export default Module;